import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal, SectionEyebrow } from "@/components/shared/Reveal";
import { CONCEPT_TERRITORIES } from "@/lib/pulseConstants";
import { Button } from "@/components/ui/button";
import { handlePulseBriefClick } from "@/lib/pulseBriefCta";

const HOMEPAGE_FEATURED_KEYS = ["kopitiam-after-dark", "product-playground", "creator-house"];

/**
 * Section 07 — Creative Territories (homepage variant).
 *
 * Shows three featured territories only. The full six-territory experience
 * remains on the /work page which continues to render CONCEPT_TERRITORIES in
 * full. Below the three cards, a compact conversion block invites visitors
 * directly into the on-page lead form.
 */
export default function ConceptTerritoriesSection() {
    const featured = HOMEPAGE_FEATURED_KEYS
        .map((k) => CONCEPT_TERRITORIES.find((c) => c.key === k))
        .filter(Boolean);

    return (
        <section
            className="section-pad relative border-t border-white/10 hidden md:block"
            data-testid="home-concept-territories-section"
        >
            <div className="container-editorial">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
                    <div>
                        <SectionEyebrow index="06" label="Creative Territories" />
                        <Reveal>
                            <h2 className="display-2 text-white text-balance">
                                Where a brief
                                <br />
                                could <span className="text-[#FF2A2A]">take us.</span>
                            </h2>
                        </Reveal>
                    </div>
                    <Reveal delay={0.1}>
                        <p className="body-md max-w-md">
                            Starting points for brands that want more than a conventional event format. Concept directions&mdash;not delivered client work.
                        </p>
                    </Reveal>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                    {featured.map((c, i) => (
                        <Reveal key={c.key} delay={0.05 * i}>
                            <motion.article
                                whileHover={{ y: -4 }}
                                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                                data-testid={`concept-territory-card-${c.key}`}
                                className="group h-full rounded-2xl border border-white/10 bg-[#0B0C10] overflow-hidden hover:border-white/25 transition-colors"
                            >
                                <div className="editorial-image aspect-[4/3] relative">
                                    <img src={c.image} alt={c.title} loading="lazy" />
                                    <div className="absolute top-4 left-4 z-10">
                                        <span className="px-2.5 py-1 rounded-full bg-black/60 backdrop-blur border border-white/15 text-[10px] font-mono uppercase tracking-widest text-white/85">
                                            {c.unlocks}
                                        </span>
                                    </div>
                                    <div className="absolute top-4 right-4 z-10 h-8 w-8 rounded-full bg-black/60 backdrop-blur border border-white/15 flex items-center justify-center group-hover:bg-[#FF2A2A] group-hover:border-[#FF2A2A] transition-colors">
                                        <ArrowUpRight size={14} className="text-white group-hover:text-black" />
                                    </div>
                                </div>
                                <div className="p-6">
                                    <div className="font-mono text-[11px] uppercase tracking-widest text-white/50 mb-2">
                                        Concept / 0{i + 1}
                                    </div>
                                    <h3 className="font-display text-2xl md:text-3xl text-white mb-3 leading-tight">
                                        {c.title}
                                    </h3>
                                    <p className="text-sm text-white/70 leading-relaxed">{c.blurb}</p>
                                </div>
                            </motion.article>
                        </Reveal>
                    ))}
                </div>

                <div className="mt-10 flex justify-center">
                    <Button asChild variant="outline" className="rounded-full border-white/20 bg-transparent hover:bg-white/5 text-white h-12 px-6">
                        <Link to="/work" data-testid="home-view-all-concepts" className="inline-flex items-center gap-2">
                            Explore all creative territories <ArrowUpRight size={16} />
                        </Link>
                    </Button>
                </div>

                {/* Compact conversion block — preserves existing visual language,
                    does not become a full section. Sits inside 07 as a tail
                    element to drive movement toward the on-page lead form. */}
                <Reveal delay={0.1}>
                    <div
                        data-testid="home-concept-territories-conversion"
                        className="mt-12 md:mt-14 rounded-2xl border border-white/10 bg-gradient-to-br from-[#0B0C10] via-[#0B0C10] to-[#170608] px-6 py-8 md:px-10 md:py-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6 relative overflow-hidden"
                    >
                        <div className="absolute -top-24 -right-16 h-64 w-64 rounded-full bg-[#FF2A2A]/10 blur-3xl pointer-events-none" aria-hidden="true" />
                        <div className="relative">
                            <h3 className="font-display text-2xl md:text-3xl text-white leading-tight">
                                Have a brief in <span className="text-[#FF2A2A]">mind?</span>
                            </h3>
                            <p className="mt-2 body-md">
                                Let&rsquo;s shape the territory together.
                            </p>
                        </div>
                        <div className="relative">
                            <Button
                                asChild
                                className="cta-glow bg-[#FF2A2A] hover:bg-[#ff4747] text-black font-semibold rounded-full h-12 md:h-12 px-6 text-base"
                                data-testid="concept-territories-conversion-cta"
                            >
                                <Link to="/contact" onClick={handlePulseBriefClick} className="inline-flex items-center gap-2">
                                    Start Your Pulse Brief <ArrowUpRight size={16} />
                                </Link>
                            </Button>
                        </div>
                    </div>
                </Reveal>
            </div>
        </section>
    );
}
